import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { getUserIpInfo, selectMapProvider } from '../../services/ipService';
import { AZURE_MAPS_KEY } from '../../config';
import { useDeviceType } from '../../hooks/useDeviceType';

const MapPreview = ({ ipData }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const mapContainer = useRef(null);
  const [userCountry, setUserCountry] = useState(null);
  const [mapProvider, setMapProvider] = useState(null);
  const [mapError, setMapError] = useState(null);
  
  if (!ipData || !ipData.loc) {
    return null;
  }

  const locParts = ipData.loc.split(',').map(coord => parseFloat(coord.trim()));
  const lat = locParts[0];
  const lon = locParts[1];
  const hasValidCoords = Number.isFinite(lat) && Number.isFinite(lon);

  // 获取用户IP信息并确定地图服务商
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userIpResult = await getUserIpInfo();
        if (userIpResult.success) {
          const userCountryCode = userIpResult.data.country;
          const targetCountryCode = ipData.country;
          
          setUserCountry(userCountryCode);
          const provider = selectMapProvider(userCountryCode, targetCountryCode);
          setMapProvider(provider);
        } else {
          // 如果获取用户IP失败，默认使用Azure Maps
          setMapProvider('azure');
        }
      } catch (error) {
        console.error('获取用户IP信息失败:', error);
        setMapProvider('azure');
      }
    };

    fetchUserInfo();
  }, [ipData.country]);

  useEffect(() => {
    if (!mapProvider || !mapContainer.current) return;

    let map;

    if (mapProvider === 'amap') {
      if (window.AMap) {
        map = new window.AMap.Map(mapContainer.current, {
          zoom: 10,
          center: [lon, lat],
        });
        
        window.AMap.plugin(['AMap.ToolBar', 'AMap.Scale'], () => {
          map.addControl(new window.AMap.ToolBar());
          map.addControl(new window.AMap.Scale());
        });
      } else {
        setMapError(t('ipInfo.map.sdkMissing'));
      }
    } else if (mapProvider === 'baidu') {
      if (window.BMapGL) {
        map = new window.BMapGL.Map(mapContainer.current);
        const point = new window.BMapGL.Point(lon, lat);
        map.centerAndZoom(point, 10);
        map.enableScrollWheelZoom();
        
        const marker = new window.BMapGL.Marker(point);
        map.addOverlay(marker);
        
        // 添加控件
        map.addControl(new window.BMapGL.ZoomControl());
        map.addControl(new window.BMapGL.ScaleControl());
      } else {
        setMapError(t('ipInfo.map.sdkMissing'));
      }
    } else if (mapProvider === 'azure') {
      if (window.atlas) {
        map = new window.atlas.Map(mapContainer.current, {
          center: [lon, lat],
          zoom: 10,
          authOptions: {
            authType: 'subscriptionKey',
            subscriptionKey: AZURE_MAPS_KEY
          }
        });
        
        map.events.add('ready', () => {
          const datasource = new window.atlas.source.DataSource();
          map.sources.add(datasource);
          
          datasource.add(new window.atlas.data.Point([lon, lat]));
          
          map.layers.add(new window.atlas.layer.BubbleLayer(datasource, null, {
            radius: 8,
            color: '#1976d2'
          }));
        });
        
        map.controls.add(new window.atlas.control.ZoomControl(), {
          position: 'bottom-right'
        });
      } else {
        setMapError(t('ipInfo.map.sdkMissing'));
      }
    }

    return () => {
      if (map) {
        if (map.dispose) map.dispose(); // For Azure Maps
        if (map.destroy) map.destroy(); // For AMap and Baidu Maps
      }
    };
  }, [mapProvider, lon, lat]);

  const { isMobile } = useDeviceType();

  const cardStyle = {
    background: colors.cardBackground,
    borderRadius: '16px',
    padding: isMobile ? '1.5rem' : '2rem',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
    backdropFilter: 'blur(10px)',
    height: 'fit-content'
  };

  const titleStyle = {
    fontSize: isMobile ? '1.2rem' : '1.5rem',
    fontWeight: '600',
    color: colors.text,
    marginBottom: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  if (!hasValidCoords) {
    return (
      <div style={cardStyle}>
        <h2 style={titleStyle}>{t('ipInfo.map.title')}</h2>
        <div style={{
          padding: '1rem',
          background: colors.isDarkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fa',
          borderRadius: '8px',
          color: colors.textSecondary
        }}>
          {t('ipInfo.map.invalidCoords')}
        </div>
      </div>
    );
  }

  const mapContainerStyle = {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    marginBottom: '1rem'
  };

  const iframeStyle = {
    width: '100%',
    height: isMobile ? '250px' : '300px',
    border: 'none',
    borderRadius: '12px'
  };

  const infoStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: isMobile ? '0.8rem' : '1rem',
    marginTop: '1rem'
  };

  const coordStyle = {
    background: colors.isDarkMode 
      ? 'rgba(102, 126, 234, 0.15)' 
      : 'rgba(102, 126, 234, 0.08)',
    border: `1px solid ${colors.isDarkMode ? 'rgba(102, 126, 234, 0.3)' : colors.border}`,
    padding: isMobile ? '0.8rem' : '1rem',
    borderRadius: '8px',
    textAlign: 'center'
  };

  const labelStyle = {
    fontSize: isMobile ? '0.7rem' : '0.8rem',
    color: colors.textSecondary,
    fontWeight: '600',
    marginBottom: '0.5rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const valueStyle = {
    fontSize: isMobile ? '0.9rem' : '1rem',
    color: colors.text,
    fontFamily: 'Monaco, Consolas, monospace',
    fontWeight: '600'
  };

  // 获取地图服务商显示名称
  const getMapProviderName = () => {
    switch (mapProvider) {
      case 'amap': return t('ipInfo.map.provider.amap');
      case 'baidu': return t('ipInfo.map.provider.baidu');
      case 'azure': return t('ipInfo.map.provider.azure');
      default: return t('ipInfo.map.provider.loading');
    }
  };

  // 获取国家显示名称
  const getCountryName = (countryCode) => {
    return countryCode === 'CN' ? t('ipInfo.map.provider.china') : countryCode;
  };

  return (
    <div style={cardStyle}>
      <h2 style={titleStyle}>
        {t('ipInfo.map.title')}
      </h2>

      {mapProvider && (
        <>
          <div style={mapContainerStyle}>
            {mapError ? (
              <div style={{
                width: '100%',
                height: isMobile ? '250px' : '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: colors.isDarkMode ? '#2d2d3a' : '#f5f5f5',
                color: colors.textSecondary,
                padding: '1rem',
                textAlign: 'center'
              }}>
                {mapError}
              </div>
            ) : (
              <div
                ref={mapContainer}
                style={{
                  width: '100%',
                  height: isMobile ? '250px' : '300px',
                  background: colors.isDarkMode ? '#2d2d3a' : '#f5f5f5'
                }}
              />
            )}
          </div>
          
          <div style={infoStyle}>
            <div style={coordStyle}>
              <div style={labelStyle}>{t('ipInfo.map.latitude')}</div>
              <div style={valueStyle}>{lat.toFixed(6)}</div>
            </div>
            <div style={coordStyle}>
              <div style={labelStyle}>{t('ipInfo.map.longitude')}</div>
              <div style={valueStyle}>{lon.toFixed(6)}</div>
            </div>
          </div>

          <div style={{
            fontSize: isMobile ? '0.7rem' : '0.8rem',
            color: colors.textSecondary,
            textAlign: 'center',
            marginTop: '1rem',
            fontStyle: 'italic'
          }}>
            {t('ipInfo.map.provider.label')}: {getMapProviderName()}
            {userCountry && ipData.country && (
              <> • {t('ipInfo.map.provider.optimized').replace(
                '{userCountry}', userCountry
              ).replace(
                '{targetCountry}', ipData.country
              )}</>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MapPreview;
